import ElasticComponentContext from '@/features/shared/ElasticComponent/ElasticComponentContext';
import {
  getDataLayerNumFilters,
  getDataLayerSearchFilters,
  getDataLayerSearchFiltersObject,
} from '@/features/shared/HOCs/withGTM';
import { useIsRevision } from '@/features/shared/Hooks';
import { parseSortByDefault } from '@/features/shared/Hooks/elasticSearch/useElasticSearch.utils';
import { FetchAllResults } from '@/features/shared/Hooks/useElasticSearch';
import { MuiButton, MuiMenu, MuiMenuItem } from '@/features/shared/material-ui';
import { SDL_SEARCH_FIELDS } from '@/utils/constants';
import { getNextId } from '@/utils/helpers';
import { reducePublicationDateChip } from '@/utils/reducers/reduceFilter';
import resolveImageUrl from '@/utils/resolvers/resolveImageUrl';
import { ExpandMore as ExpandMoreIcon, Print, SaveAlt } from '@material-ui/icons';
import clsx from 'clsx';
import printJS from 'print-js';
import qs from 'query-string';
import React, {
  memo,
  MouseEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import CsvDownload from 'react-csv-downloader';
import ReactDOMServer from 'react-dom/server';
import TagManager from 'react-gtm-module';
import { getRowItem, wrapData } from '../SDLtable/shared/PaginatedTable/SDLpaginatedTable.mapper';
import { PrintToolBarProps } from './PrintToolBar.interface';
import useStyles, { classesPdf } from './PrintToolBar.styles';

type PrintType = 'PRINT' | 'PRINT_ALL';
interface AdaptResultsProps {
  results: any[];
  headings?: any[];
  deviceType?: string;
}
const adaptResults = ({ results = [], headings = [], deviceType = '' }: AdaptResultsProps) =>
  wrapData({ data: results }).map((row) =>
    headings.reduce(
      (state, el) => ({ ...state, [el.key]: getRowItem({ row, key: el.key, deviceType }) }),
      headings.length,
    ),
  );

const processedPrintResults = ({
  results = [],
  headings = [],
  deviceType = '',
}: AdaptResultsProps): any =>
  wrapData({ data: results }).map((row: any) =>
    headings.map((heading: any) => {
      const rowItem = getRowItem({ row, key: heading.key, deviceType });

      if (/^io$/.test(heading.key)) return rowItem.replace('/', '|');

      return rowItem;
    }),
  );

const PrintToolBar = ({
  data,
  searchKey,
  theme,
  queries,
  labels,
  config,
  deviceType = '',
}: PrintToolBarProps) => {
  const isRevision = useIsRevision();
  const [idDownloadAs] = useState(getNextId('download-as-'));
  const classes = useStyles({});
  const headings = useMemo(
    () => [...(data?.headings?.map((heading: any) => heading.value) || [])],
    [data?.headings],
  );
  const printOptionLabel = labels?.download_as;
  const [printData, setPrintData] = useState<{ headings: any; results: any[] }>({
    headings,
    results: [],
  });
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [printOption] = useState({ label: printOptionLabel });
  const [isLoading, setIsLoading] = useState(false);
  const [queryFilters, setQueryFilters] = useState([]);
  const [currentFilters, setCurrentFilters] = useState([]);
  const [csvRes, setCsvRes] = useState<any>(null);
  const {
    engine = 'qa-sdl',
    facets,
    queryParams,
    pagination,
  } = useContext(ElasticComponentContext);
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const { category_type = 'camera' } = qs.parse(window.location.search);

  const csvResults = (res: any) => {
    const strings: any = [];
    res.map((item: any) => {
      strings.push(Array.isArray(item) ? item.map((i: any) => `"${i}"`) : `"${item}"`);
      return strings;
    });
    return strings;
  };

  useEffect(() => {
    setQueryFilters(queries.filters);
    setCsvRes(csvResults(printData.results));
  }, [queries.category_type, queries.filters, printData.results]);

  useEffect(() => {
    const removeEmptyValues = (obj: any) => {
      const newObj: any = {};
      Object.keys(obj).forEach((prop: any) => {
        if (obj[prop] && obj[prop].length > 0) {
          newObj[prop] = obj[prop];
        }
      });
      return newObj;
    };

    const objectsToArray = (obj: any) => {
      const res: any = [];
      const keys = Object.keys(obj);
      keys.forEach((key) => {
        res.push({
          [key]: obj[key],
        });
      });
      res.unshift({
        category_type: queries.category_type,
      });
      return res;
    };

    const propFilters = removeEmptyValues(queryFilters);
    const cleanFilters = (mockfilters: any) => {
      if (Object.keys(mockfilters).length === 0) {
        return { category_type: queries.category_type };
      }

      return objectsToArray(mockfilters);
    };
    setCurrentFilters(cleanFilters(propFilters));
  }, [queries, queries.filters, queryFilters]);

  const getAllDevices = React.useCallback(
    async ({ type }: { type: PrintType }) => {
      const DEFAULT_SEARCH_PARAMS = {
        query: '',
        filters: { all: [{ category_type }] },
        sort: parseSortByDefault(queryParams.sortBy, queryParams.sortType),
      };
      const SEARCH_PARAMS = {
        query: queries?.q || '',
        filters: { all: currentFilters },
        sort: parseSortByDefault(queryParams.sortBy, queryParams.sortType),
      };
      const searchParams = /^PRINT$/.test(type) ? SEARCH_PARAMS : DEFAULT_SEARCH_PARAMS;

      setIsLoading(true);

      const { results } = await FetchAllResults({
        engine,
        searchKey,
        searchParams,
        search_fields: SDL_SEARCH_FIELDS,
      });

      setIsLoading(false);

      return results;
    },
    [
      category_type,
      currentFilters,
      engine,
      queries?.q,
      queryParams.sortBy,
      queryParams.sortType,
      searchKey,
    ],
  );

  const handleOpenDownloadOptions = React.useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      setIsLoading(true);
      setAnchorEl(event.currentTarget);

      getAllDevices({ type: 'PRINT' })
        .then((results: any) => {
          setPrintData({
            ...printData,
            results: processedPrintResults({ results, headings: data.headings, deviceType }),
          });
          setCsvRes(csvResults(printData.results));
        })
        .finally(() => setIsLoading(false));
    },
    [data.headings, deviceType, getAllDevices, printData],
  );

  const filters = React.useMemo(() => queryParams?.filters, [queryParams?.filters]);
  const searchFiltersObject = React.useMemo(
    () => getDataLayerSearchFiltersObject(filters),
    [filters],
  );
  const searchFilters = React.useMemo(() => getDataLayerSearchFilters(filters), [filters]);
  const numSearchFilters = React.useMemo(() => getDataLayerNumFilters(filters), [filters]);

  const handleCloseDownloadOptions = React.useCallback(
    (dataLayerFileType = '') => {
      setAnchorEl(null);

      if (!dataLayerFileType) return;
      const dataLayer = {
        event: 'trackEvent-sdlListDownload',
        event_name: 'sdl_list_download',
        position: 'sdl',
        ...(dataLayerFileType === 'csv' && { file_name: 'genetec_supported_device_list.csv' }),
        file_extension: dataLayerFileType,
        link_text:
          dataLayerFileType === 'csv'
            ? labels?.download_csv || 'Download CSV'
            : labels?.download_pdf || 'Download PDF',
        link_domain: window.location.host,
        search_term: queryParams?.q?.trim(),
        num_search_result: pagination?.totalResults || 0,
        index_num: String(pagination?.currentPage || 1),
        is_revision: isRevision,
        sdl_category: queryParams?.category_type,
        ...(!!searchFilters && { search_filters: searchFilters }),
        num_search_filters: numSearchFilters,
        ...searchFiltersObject,
      };
      TagManager.dataLayer({ dataLayer });
    },
    [
      isRevision,
      labels?.download_csv,
      labels?.download_pdf,
      numSearchFilters,
      pagination?.currentPage,
      pagination?.totalResults,
      queryParams?.category_type,
      queryParams?.q,
      searchFilters,
      searchFiltersObject,
    ],
  );

  const printPropsData = React.useCallback((deviceHeaders: any) => {
    const printProps = [] as any;
    if (deviceHeaders) {
      Object.values(deviceHeaders).map((value: any) => {
        const jsonProp = { field: value.key, displayName: value.value };
        const laststep = printProps.push(jsonProp);
        if (laststep.length <= 8) {
          return laststep;
        }
        return undefined;
      });
    } else {
      return printProps;
    }
    return printProps;
  }, []);

  const renderDefaultLabel = ({
    item,
  }: {
    item: { key: string; value: string; count: number | string; label?: string };
  }) => item?.label ?? item.value;

  const header = useCallback(
    ({ type }: { type: PrintType }) => {
      const hasLogo = !!config?.genetec_pdf_logo;
      const logo = resolveImageUrl({ raw: config?.genetec_pdf_logo });
      const chips =
        facets
          .reduce<{ key: string; value: string; label?: string }[]>((prev, { options, name }) => {
            const mappedOptions = options
              .filter((opt) => opt.selected)
              .map(({ value }) => ({ key: name, value }));

            // TODO: Implement a Map as strategy pattern for multiple reducers.
            return /publication_date/.test(name)
              ? [...prev, ...reducePublicationDateChip({ list: mappedOptions })]
              : [...prev, ...mappedOptions];
          }, [])
          .map((item: any) => renderDefaultLabel({ item })) || [];
      const keywords = queryParams?.keywords || [];
      const hasFilters = /^PRINT$/.test(type) && (!!chips.length || !!keywords.length);

      const render = (
        <>
          <section style={classesPdf.header}>
            <h3 style={classesPdf.title}>
              {labels?.sdl || 'Supported Device List'} -{' '}
              {(labels[queries.category_type] || queries.category_type).toUpperCase()}
            </h3>
            {hasLogo && <img src={logo} alt="Genetec Logo" style={classesPdf.logo} />}
          </section>
          {hasFilters && (
            <section style={classesPdf.filters}>
              <strong>{labels?.active_filters}: </strong>
              <span>{[...chips, ...keywords].join(', ')}</span>
            </section>
          )}
        </>
      );

      return ReactDOMServer.renderToStaticMarkup(render);
    },
    [config, queries.category_type, labels, facets, queryParams],
  );

  const handlePrint = React.useCallback(
    ({ results, type }: { results: any; type: PrintType }) => {
      const printable = adaptResults({ results, headings: data.headings, deviceType });
      const properties = printPropsData(data.headings);

      printJS({
        printable,
        properties,
        header: header({ type }),
        type: 'json',
      });
    },
    [data.headings, deviceType, header, printPropsData],
  );

  const printDevices = React.useCallback(async () => {
    const results = await getAllDevices({ type: 'PRINT' });
    handlePrint({ results, type: 'PRINT' });

    const dataLayer = {
      event: 'trackEvent-sdlListPrintResult',
      event_name: 'sdl_list_print_result',
      position: 'sdl',
      ...(!!queryParams?.q?.trim() && { search_term: queryParams?.q?.trim() }),
      num_search_result: pagination?.totalResults || 0,
      sdl_category: queryParams?.category_type,
      index_num: String(pagination?.currentPage || 1),
      is_revision: isRevision,
      ...(!!searchFilters && { search_filters: searchFilters }),
      num_search_filters: numSearchFilters,
      ...searchFiltersObject,
    };
    TagManager.dataLayer({ dataLayer });
  }, [
    getAllDevices,
    handlePrint,
    isRevision,
    numSearchFilters,
    pagination?.currentPage,
    pagination?.totalResults,
    queryParams?.category_type,
    queryParams?.q,
    searchFilters,
    searchFiltersObject,
  ]);

  const printAllDevices = React.useCallback(
    async (isDataLayerEnabled = true) => {
      const results = await getAllDevices({ type: 'PRINT_ALL' });
      handlePrint({ results, type: 'PRINT_ALL' });

      if (!isDataLayerEnabled) return;
      const dataLayer = {
        event: 'trackEvent-sdlListPrintAll',
        event_name: 'sdl_list_print_all',
        position: 'sdl',
        ...(!!queryParams?.q?.trim() && { search_term: queryParams?.q?.trim() }),
        num_search_result: pagination?.totalResults || 0,
        sdl_category: queryParams?.category_type,
      };
      TagManager.dataLayer({ dataLayer });
    },
    [
      getAllDevices,
      handlePrint,
      pagination?.totalResults,
      queryParams?.category_type,
      queryParams?.q,
    ],
  );

  const rootClasses = clsx(classes.root, classes.list, `${theme}-theme`);
  return (
    <ul className={rootClasses}>
      <li>
        <MuiButton
          aria-controls={idDownloadAs}
          aria-haspopup="true"
          className={clsx(classes.sortByButton, classes.listButton, 'gtm-download')}
          onClick={handleOpenDownloadOptions}
          variant="text"
          size="small"
          startIcon={<SaveAlt className={classes.listButtonIcon} />}
        >
          <div>
            <SaveAlt className={classes.listButtonIconMobile} />
            {printOption?.label}
            <ExpandMoreIcon className={classes.listButtonIconEndMobile} />
          </div>
        </MuiButton>
        {!!anchorEl && (
          <MuiMenu
            onClose={() => handleCloseDownloadOptions()}
            id={idDownloadAs}
            open={!!anchorEl}
            anchorEl={anchorEl}
            keepMounted
          >
            <MuiMenuItem
              disabled={isLoading}
              onClick={() => {
                handleCloseDownloadOptions('csv');
              }}
            >
              <CsvDownload
                filename="genetec_supported_device_list"
                columns={printData.headings}
                datas={csvRes}
              >
                <span>{labels?.download_csv || 'Download CSV'}</span>
              </CsvDownload>
            </MuiMenuItem>

            <MuiMenuItem
              disabled={isLoading}
              onClick={() => {
                printAllDevices(false);
                handleCloseDownloadOptions('pdf');
              }}
            >
              {labels?.download_pdf || 'Download PDF'}
            </MuiMenuItem>
          </MuiMenu>
        )}
      </li>
      <li>
        <MuiButton
          className={clsx(classes.listButton, 'gtm-print')}
          aria-haspopup="false"
          variant="text"
          size="small"
          startIcon={<Print className={classes.listButtonIcon} />}
          onClick={printDevices}
          disabled={isLoading}
        >
          <div>
            <Print className={classes.listButtonIconMobile} />
            <div>{labels?.print_label}</div>
          </div>
        </MuiButton>
      </li>
      <li>
        <MuiButton
          className={clsx(classes.listButton, 'gtm-print')}
          aria-haspopup="false"
          variant="text"
          size="small"
          startIcon={<Print className={classes.listButtonIcon} />}
          onClick={() => printAllDevices()}
          disabled={isLoading}
        >
          <div>
            <Print className={classes.listButtonIconMobile} />
            <div>{labels?.print_all_devices}</div>
          </div>
        </MuiButton>
      </li>
    </ul>
  );
};

export default memo(PrintToolBar);
